import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FolderTree, Edit, Trash2, RefreshCw, FilterX } from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, AppButton, ConfirmModal, AppModal, FormField, Input, Textarea,
  NativeSelect, FilterToolbar, AppDataTable, ActionMenu, ActionMenuItem,
  ActionMenuDivider, applyColumnFilters, sortRows, isColumnFilterActive,
} from "../ui";
import { DEPRECIATION_METHODS } from "./assetConstants";

/**
 * AssetCategoriesPage — asset categories and their default depreciation.
 *
 * The table is `ui/AppDataTable` with the header controls on, the same shape as
 * `/sa/institutes`: the endpoint returns every category in one response, so
 * filtering, sorting and paging all happen in the browser over the whole list
 * rather than over one page of it.
 */

const PAGE_SIZE = 25;

const DASH = <span className="tw-text-content-subtle">—</span>;

/** `DEPRECIATION_METHODS` is already `{ value, label }` — the shape a set filter
 *  and a `NativeSelect` both take, so neither restates it. */
const METHOD_LABEL = Object.fromEntries(
  DEPRECIATION_METHODS.map((m) => [m.value, m.label])
);

const blank = {
  name: "", code: "", description: "",
  defaultDepreciationMethod: "none",
  defaultUsefulLifeMonths: 0,
  defaultSalvagePercent: 0,
  isActive: true,
};

const AssetCategoriesPage = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [seedLoading, setSeedLoading] = useState(false);

  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);

  const showToast = useCallback(
    (message, type = "success") => setToast({ show: true, message, type }),
    [],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/assets/categories");
      setItems(data.categories || []);
    } catch {
      showToast("Failed to load categories.", "error");
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(blank); setShowModal(true); };
  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name || "",
      code: cat.code || "",
      description: cat.description || "",
      defaultDepreciationMethod: cat.defaultDepreciationMethod || "none",
      defaultUsefulLifeMonths: cat.defaultUsefulLifeMonths || 0,
      defaultSalvagePercent: cat.defaultSalvagePercent || 0,
      isActive: cat.isActive !== false,
    });
    setShowModal(true);
  };

  const columns = useMemo(() => [
    {
      key: "code",
      label: "Code",
      sortable: true,
      filter: "text",
      filterValue: (c) => c.code || "",
      render: (c) => (c.code
        ? <span className="tw-font-mono tw-text-[0.75rem] tw-text-content-subtle">{c.code}</span>
        : DASH),
    },
    {
      key: "name",
      label: "Name",
      sortable: true,
      filter: { type: "text", placeholder: "Name or description…" },
      // The description is not a column of its own, but it is the other thing
      // someone looks a category up by.
      filterValue: (c) => `${c.name || ""} ${c.description || ""}`,
      sortValue: (c) => c.name || "",
      render: (c) => <span className="tw-font-medium tw-text-content">{c.name}</span>,
    },
    {
      key: "defaultDepreciationMethod",
      label: "Default Method",
      sortable: true,
      filter: { type: "set", options: DEPRECIATION_METHODS },
      filterValue: (c) => c.defaultDepreciationMethod || "none",
      // The LABEL, not the enum — "Written-Down Value (WDV)" is what the filter
      // offers, so it has to be what the cell shows.
      render: (c) => METHOD_LABEL[c.defaultDepreciationMethod || "none"] || "None",
    },
    {
      key: "defaultUsefulLifeMonths",
      label: "Useful Life",
      align: "right",
      sortable: true,
      filter: "number",
      // ⚠️ Zero, never undefined — the number filter rejects a non-finite value,
      // so a category with no default life would be invisible to its own filter.
      filterValue: (c) => c.defaultUsefulLifeMonths ?? 0,
      // The unit moves INSIDE the branch: the old cell rendered "— mo".
      render: (c) => (c.defaultUsefulLifeMonths
        ? <span className="tw-tabular-nums">{c.defaultUsefulLifeMonths} mo</span>
        : DASH),
    },
    {
      key: "defaultSalvagePercent",
      label: "Salvage %",
      align: "right",
      sortable: true,
      filter: "number",
      filterValue: (c) => c.defaultSalvagePercent ?? 0,
      render: (c) => <span className="tw-tabular-nums">{c.defaultSalvagePercent || 0}%</span>,
    },
    {
      key: "actions",
      label: "",
      width: 48,
      pinned: "right",
      resizable: false,
      // ⚠️ The row is clickable, so every control in this cell must stop the
      // click reaching it — otherwise opening the menu also opens the editor.
      render: (c) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu label={`Actions for ${c.name}`}>
            <ActionMenuItem onClick={() => openEdit(c)}>
              <Edit size={14} /> Edit
            </ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem danger onClick={() => setDeleteTarget(c)}>
              <Trash2 size={14} /> Remove
            </ActionMenuItem>
          </ActionMenu>
        </div>
      ),
    },
  ], []);

  const filtersOn = Object.values(columnFilters).some(isColumnFilterActive);

  const visible = useMemo(
    () => sortRows(applyColumnFilters(items, columns, columnFilters), columns, sort),
    [items, columns, columnFilters, sort],
  );

  // A filter or a sort can leave the current page past the end of the results.
  useEffect(() => { setPage(1); }, [columnFilters, sort]);
  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageRows = useMemo(
    () => visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [visible, page],
  );

  const handleSeedDefaults = async () => {
    setSeedLoading(true);
    try {
      const { data } = await apiClient.post("/assets/categories/seed-defaults");
      showToast(data.message);
      if (data.inserted > 0) fetchData();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load defaults.", "error");
    }
    setSeedLoading(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast("Name is required.", "error");
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await apiClient.put(`/assets/categories/${editing._id}`, form);
        showToast("Category updated.");
      } else {
        await apiClient.post("/assets/categories", form);
        showToast("Category created.");
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      showToast(err?.response?.data?.message || "Save failed.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/assets/categories/${deleteTarget._id}`);
      showToast("Category removed.");
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      showToast(err?.response?.data?.message || "Delete failed.", "error");
      setDeleteTarget(null);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />

      <PageHeader
        backButton
        onBack={() => navigate("/assets")}
        icon={FolderTree}
        title="Asset Categories"
        description="Organize assets into categories with default depreciation"
      />

      <AppDataTable
        fill={false}
        resizable
        storageKey="asset-categories"
        toolbar={
          <FilterToolbar
            flush
            actions={
              <>
                <AppButton size="sm" variant="secondary" onClick={handleSeedDefaults} disabled={seedLoading}>
                  <RefreshCw size={16} /> {seedLoading ? "Loading…" : "Load Defaults"}
                </AppButton>
                <AppButton size="sm" onClick={openCreate}><Plus size={16} /> New Category</AppButton>
              </>
            }
          >
            <span className="tw-text-[0.8125rem] tw-text-content-subtle">
              {visible.length === items.length
                ? `${items.length} categor${items.length === 1 ? "y" : "ies"}`
                : `${visible.length} of ${items.length}`}
            </span>
            {filtersOn && (
              <AppButton size="sm" variant="outline" onClick={() => setColumnFilters({})}>
                <FilterX size={14} /> Clear filters
              </AppButton>
            )}
          </FilterToolbar>
        }
        columns={columns}
        rows={pageRows}
        rowKey={(c) => c._id}
        loading={loading}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) =>
          setColumnFilters((prev) => {
            // Cleared filters are REMOVED, not stored as undefined — a key
            // sitting there holding nothing would keep "Clear filters" on
            // screen after the last one was cleared.
            const next = { ...prev };
            if (value === undefined) delete next[key];
            else next[key] = value;
            return next;
          })
        }
        onRowClick={openEdit}
        emptyMessage={filtersOn
          ? "No category matches these filters."
          : "No categories yet. Create your first asset category."}
        pagination={totalPages > 1 ? {
          page,
          totalPages,
          total: visible.length,
          pageSize: PAGE_SIZE,
          onChange: setPage,
          infoLabel: "categories",
        } : undefined}
      />

      <AppModal
        show={showModal}
        onHide={() => setShowModal(false)}
        title={editing ? "Edit Category" : "New Category"}
        size="md"
        /* ⚠️ The modal's own footer slot, NOT `FormActionBar` — that one is
           `position: fixed` for a full-page form. */
        footer={
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
            <AppButton variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
              Cancel
            </AppButton>
            <AppButton onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </AppButton>
          </div>
        }
      >
        <div className="tw-flex tw-flex-col tw-gap-3">
          <FormField label="Name" required>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </FormField>
          <FormField label="Code" hint="A short tag, e.g. LAP or BRD.">
            <Input
              value={form.code}
              maxLength={6}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            />
          </FormField>
          <FormField label="Description">
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </FormField>
          <FormField label="Default Depreciation Method">
            <NativeSelect
              options={DEPRECIATION_METHODS}
              value={form.defaultDepreciationMethod}
              onChange={(e) => setForm((f) => ({ ...f, defaultDepreciationMethod: e.target.value }))}
            />
          </FormField>
          <FormField label="Default Useful Life (months)">
            <Input
              type="number"
              min="0"
              value={form.defaultUsefulLifeMonths}
              onChange={(e) => setForm((f) => ({ ...f, defaultUsefulLifeMonths: Number(e.target.value) || 0 }))}
            />
          </FormField>
          <FormField label="Default Salvage %">
            <Input
              type="number"
              min="0"
              max="100"
              value={form.defaultSalvagePercent}
              onChange={(e) => setForm((f) => ({ ...f, defaultSalvagePercent: Number(e.target.value) || 0 }))}
            />
          </FormField>
        </div>
      </AppModal>

      <ConfirmModal
        show={!!deleteTarget}
        title="Remove Category?"
        message={deleteTarget ? `Delete "${deleteTarget.name}"?` : ""}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onHide={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AssetCategoriesPage;
